import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteField,
  getDoc,
  where,
  type Firestore,
} from 'firebase/firestore';
import { LIA_MODEL_AUTO, liaModelChoice } from './lia-models.js';

/** Reglage non secret de l'acces IA de l'organisation. Ferme par defaut. */
export interface IaSettings {
  enabled: boolean;
  launcher: 'mascot' | 'bubble';
  /**
   * Modele de Lia choisi par le proprietaire (`LIA_MODEL_CHOICES`), `auto` pour Jev.
   * `null` : le defaut du projet. L'ancien interrupteur `jevBeta` se lit comme `auto`.
   */
  model: string | null;
}

/**
 * LA PORTEE D'UNE CONNEXION MCP (`docs/socle/fiche-suivi.md` section 5) :
 * `lecture` (les lectures de la liste reduite), `reduite` (la liste reduite,
 * le defaut), `complete` (tout le catalogue selon les roles). Le mode de l'org
 * reste le plafond ; le serveur la relit a chaque appel
 * (`functions/src/actions/exposition.ts`, recopie de ces trois valeurs).
 */
export const PORTEES_CONNEXION_IA = ['lecture', 'reduite', 'complete'] as const;
export type IaConnectionPortee = typeof PORTEES_CONNEXION_IA[number];
/** Absente en base, ou illisible : `reduite`, comme le serveur. */
export const PORTEE_CONNEXION_IA_PAR_DEFAUT: IaConnectionPortee = 'reduite';

/**
 * LE MODE D'EXPOSITION MCP DE L'ORG (`docs/socle/mcp-exposition.md`), le
 * PLAFOND de toute portee : `restreint` (la liste reduite, le defaut) ou
 * `complet`. Ecrit par le serveur seul (`orgs/{orgId}/actionExposure/default`),
 * lisible de tout membre actif (rules). Memes valeurs que
 * `ACTION_EXPOSURE_MODES` de `functions/src/actions/exposition.ts`.
 */
export const MODES_EXPOSITION_IA = ['restreint', 'complet'] as const;
export type IaModeExposition = typeof MODES_EXPOSITION_IA[number];
/** Sans document, ou illisible : `restreint`, comme le serveur. */
export const MODE_EXPOSITION_IA_PAR_DEFAUT: IaModeExposition = 'restreint';

export interface IaConnection {
  id: string;
  uid: string;
  email: string | null;
  clientName: string;
  connectedAt: Date | null;
  lastSeenAt: Date | null;
  revokedAt: Date | null;
  /** La portee de la connexion ; absente en base, `reduite`. */
  portee: IaConnectionPortee;
}

export interface IaSettingsRepository {
  observer(onValue: (settings: IaSettings) => void, onError?: (error: unknown) => void): () => void;
  setEnabled(enabled: boolean): Promise<void>;
  setLauncher(launcher: IaSettings['launcher']): Promise<void>;
  setModel(model: string | null): Promise<void>;
  connections(includeAll?: boolean): Promise<IaConnection[]>;
  revokeConnection(id: string): Promise<void>;
  /** Change la portee d'une connexion ouverte : le PROPRIETAIRE de l'org seul (rules), trace au journal. */
  setConnectionScope(id: string, portee: IaConnectionPortee): Promise<void>;
  /** Le mode d'exposition MCP de l'org, relu au serveur ; sans document, `restreint`. */
  modeExposition(): Promise<IaModeExposition>;
}

function date(value: unknown): Date | null {
  return value != null && typeof value === 'object' && 'toDate' in value &&
    typeof value.toDate === 'function' ? value.toDate() as Date : null;
}

export function createIaSettingsRepository(db: Firestore, getActorId: () => string, orgId: string): IaSettingsRepository {
  const settingsRef = doc(db, 'orgs', orgId, 'aiSettings', 'default');
  const connectionsRef = collection(db, 'orgs', orgId, 'aiConnections');
  const expositionRef = doc(db, 'orgs', orgId, 'actionExposure', 'default');
  return {
    observer(onValue, onError) {
      return onSnapshot(settingsRef, (snapshot) => {
        onValue({ enabled: snapshot.exists() && snapshot.data()['enabled'] === true,
          launcher: snapshot.data()?.['launcher'] === 'bubble' ? 'bubble' : 'mascot',
          model: liaModelChoice(snapshot.data()?.['model']) ?? (snapshot.data()?.['jevBeta'] === true ? LIA_MODEL_AUTO : null) });
      }, onError);
    },
    async setEnabled(enabled) {
      await setDoc(settingsRef, {
        orgId,
        enabled,
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      }, { merge: true });
    },
    async setLauncher(launcher) {
      await runTransaction(db, async (transaction) => {
        const current = await transaction.get(settingsRef);
        transaction.set(settingsRef, {
          orgId,
          enabled: current.exists() && current.data()['enabled'] === true,
          launcher,
          updatedAt: serverTimestamp(),
          updatedBy: getActorId(),
        }, { merge: true });
      });
    },
    async setModel(model) {
      await runTransaction(db, async (transaction) => {
        const current = await transaction.get(settingsRef);
        transaction.set(settingsRef, {
          orgId,
          enabled: current.exists() && current.data()['enabled'] === true,
          // Le menu remplace l'ancien interrupteur Jev : il ne doit plus rien decider.
          model: model === null ? deleteField() : model,
          jevBeta: deleteField(),
          updatedAt: serverTimestamp(),
          updatedBy: getActorId(),
        }, { merge: true });
      });
    },
    async connections(includeAll = false) {
      const snapshot = await getDocs(includeAll ? connectionsRef : query(connectionsRef, where('uid', '==', getActorId())));
      return snapshot.docs.map((entry) => {
        const data = entry.data();
        return {
          id: entry.id,
          uid: typeof data['uid'] === 'string' ? data['uid'] : '',
          email: typeof data['email'] === 'string' ? data['email'] : null,
          clientName: typeof data['clientName'] === 'string' ? data['clientName'] : 'Client MCP',
          connectedAt: date(data['connectedAt']),
          lastSeenAt: date(data['lastSeenAt']),
          revokedAt: date(data['revokedAt']),
          portee: (PORTEES_CONNEXION_IA as readonly unknown[]).includes(data['portee']) ? data['portee'] as IaConnectionPortee : PORTEE_CONNEXION_IA_PAR_DEFAUT,
        };
      });
    },
    async revokeConnection(id) {
      await updateDoc(doc(connectionsRef, id), { revokedAt: serverTimestamp(), revokedBy: getActorId() });
    },
    async setConnectionScope(id, portee) {
      if (!(PORTEES_CONNEXION_IA as readonly string[]).includes(portee)) throw new Error(`Portée de connexion inconnue : ${String(portee)}.`);
      await updateDoc(doc(connectionsRef, id), { portee, updatedAt: serverTimestamp(), updatedBy: getActorId() });
    },
    async modeExposition() {
      const snapshot = await getDoc(expositionRef);
      const mode: unknown = snapshot.exists() ? snapshot.data()['mode'] : undefined;
      return (MODES_EXPOSITION_IA as readonly unknown[]).includes(mode) ? mode as IaModeExposition : MODE_EXPOSITION_IA_PAR_DEFAUT;
    },
  };
}
